import { lazy, Suspense, useEffect, useRef } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  Link as RouterLink,
  useLocation,
} from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import { useActiveFarmContext, useUserContext } from "@/contexts";
import PageLoading from "@/pages/loading";

// Lazy load all page components for code splitting
const PageHome = lazy(() => import("@/pages/home"));
const PageManageFieldsV2 = lazy(() => import("@/pages/manageFieldsV2"));
const PageFarms = lazy(() => import("@/pages/farms"));
const PageFieldsAnalyseV2 = lazy(() => import("@/pages/fieldsAnalyseV2"));
const PageFarmCover = lazy(() => import("@/pages/farmCover"));
const PageFieldsDaysSince = lazy(() => import("@/pages/fieldsDaysSince"));
const PageFieldsAnalyseV3 = lazy(() => import("@/pages/fieldsAnalyseV3"));
const PageFieldsAnalyseV4 = lazy(() => import("@/pages/fieldsAnalyseV4"));
const PageFieldDashboard = lazy(() => import("@/pages/fieldDashboard"));
const PageFieldDashboardForecast = lazy(
  () => import("@/pages/fieldDashboardForecast")
);
const PageWeatherForecast = lazy(() => import("@/pages/weatherForecast"));
const PageFields = lazy(() => import("@/pages/fields"));
const PageCirculations = lazy(() => import("@/pages/circulations"));
const PageScan = lazy(() => import("@/pages/scan"));
const PageNotFound = lazy(() => import("@/pages/notFound"));
const PageAcceptInvitation = lazy(() => import("@/pages/acceptInvitation"));
const PageInvitations = lazy(() => import("@/pages/invitations"));
const PageAdmin = lazy(() => import("@/pages/admin"));
const PageForecastAnalysis = lazy(() => import("@/pages/forecastAnalysis"));
const PageSatelliteDetectionAnalysis = lazy(
  () => import("@/pages/satelliteDetectionAnalysis")
);
const PageProfile = lazy(() => import("@/pages/profile"));
const PageGuide = lazy(() => import("@/pages/guide"));

const BreadcrumbSX = {
  color: "text.secondary",
  fontSize: "0.875rem",
  fontWeight: 500,
  "&:hover": {
    color: "text.primary",
  },
};

const RoutesBox = ({ children, location }) => (
  <Box className="routes-box" sx={{ width: "100%", p: 2 }}>
    <MyBreadCrumbs location={location} />
    <Suspense fallback={<PageLoading />}>{children}</Suspense>
  </Box>
);

const RequireAuth = () => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const location = useLocation();
  const hasRequestedLogin = useRef(false);

  useEffect(() => {
    if (isLoading || isAuthenticated || hasRequestedLogin.current) {
      return;
    }

    hasRequestedLogin.current = true;
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    loginWithRedirect({
      appState: {
        returnTo: returnTo || "/",
      },
    });
  }, [
    isAuthenticated,
    isLoading,
    location.hash,
    location.pathname,
    location.search,
    loginWithRedirect,
  ]);

  if (isLoading) {
    return <PageLoading loadingText={t("checkingAuthentication")} />;
  }

  if (!isAuthenticated) {
    return <PageLoading loadingText={t("redirectingToLogin")} />;
  }

  return <Outlet />;
};

const RequireActiveFarm = () => {
  const { t } = useTranslation();
  const { activeFarm, activeFarmId, farmsLoading, hasLoadedFarms } =
    useActiveFarmContext();

  if (farmsLoading || (activeFarmId && !hasLoadedFarms)) {
    return <PageLoading loadingText={t("loadingFarms")} />;
  }

  if (!activeFarm) {
    return <PageFarms />;
  }

  return <Outlet />;
};

const AdminOnlyRoute = () => {
  const { t } = useTranslation();
  const { isAdmin, loading } = useUserContext();

  if (loading) {
    return <PageLoading loadingText={t("checkingAccess")} />;
  }

  if (!isAdmin) {
    return <Navigate to="/farms" replace />;
  }

  return <Outlet />;
};

const ForecastDashboardOnlyRoute = () => {
  const { t } = useTranslation();
  const { forecastDashboardEnabled, loading } = useUserContext();

  if (loading) {
    return <PageLoading loadingText={t("checkingAccess")} />;
  }

  if (!forecastDashboardEnabled) {
    return <Navigate to="/farms" replace />;
  }

  return <Outlet />;
};

// Separate from AdminOnlyRoute on purpose: allowlisted non-admins must be able to enter.
const ForecastAnalysisOnlyRoute = () => {
  const { t } = useTranslation();
  const { forecastAnalysisEnabled, loading } = useUserContext();

  if (loading) {
    return <PageLoading loadingText={t("checkingAccess")} />;
  }

  if (!forecastAnalysisEnabled) {
    return <Navigate to="/farms" replace />;
  }

  return <Outlet />;
};

const MyBreadCrumbs = ({ location }) => {
  const { t } = useTranslation();
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const formatCrumb = (segment) =>
    t(`routes.${segment}`, {
      defaultValue: segment.charAt(0).toUpperCase() + segment.slice(1),
    });

  // Don't show breadcrumbs on home page
  if (pathSegments.length === 0) return null;

  return (
    <Box sx={{ pb: 1 }}>
      <Breadcrumbs
        aria-label="breadcrumb"
        separator="›"
        sx={{
          "& .MuiBreadcrumbs-separator": {
            color: "text.disabled",
            mx: 1,
          },
        }}
      >
        <Link component={RouterLink} to="/" underline="hover" sx={BreadcrumbSX}>
          {t("nav.home")}
        </Link>

        {pathSegments.slice(0, -1).map((segment, index) => {
          const path = `/${pathSegments.slice(0, index + 1).join("/")}`;
          return (
            <Link
              key={path}
              component={RouterLink}
              to={path}
              underline="hover"
              sx={BreadcrumbSX}
            >
              {formatCrumb(segment)}
            </Link>
          );
        })}

        <Typography
          sx={{
            color: "text.primary",
            fontSize: "0.875rem",
            fontWeight: 500,
          }}
        >
          {formatCrumb(pathSegments[pathSegments.length - 1])}
        </Typography>
      </Breadcrumbs>
    </Box>
  );
};

// ErrorBoundary Test Component
// const TestErrorPage = () => { throw new Error("This is a test error to demonstrate the error boundary!"); };

const MyRoutes = () => {
  const location = useLocation();

  return (
    <RoutesBox location={location}>
      <Routes>
        <Route path="/" element={<PageHome />} />
        <Route path="/accept-invitation" element={<PageAcceptInvitation />} />
        <Route element={<RequireAuth />}>
          <Route path="/farms" element={<PageFarms />} />
          <Route path="/onboarding-guide" element={<PageGuide />} />
          <Route path="/invitations" element={<PageInvitations />} />
          <Route path="/profile" element={<PageProfile />} />
          <Route element={<ForecastAnalysisOnlyRoute />}>
            <Route
              path="/forecast-analysis"
              element={<PageForecastAnalysis />}
            />
            <Route
              path="/satellite-detection-analysis"
              element={<PageSatelliteDetectionAnalysis />}
            />
          </Route>
          <Route element={<AdminOnlyRoute />}>
            <Route path="/circulations" element={<PageCirculations />} />
            <Route path="/scan" element={<PageScan />} />
            <Route path="/admin" element={<PageAdmin />} />
          </Route>
          <Route element={<RequireActiveFarm />}>
            <Route path="/farms/analysis" element={<PageFieldsAnalyseV2 />} />
            <Route path="/farms/cover" element={<PageFarmCover />} />
            <Route path="/farms/days-since" element={<PageFieldsDaysSince />} />
            <Route path="/farms/fields" element={<PageFields />} />
            <Route path="/farms/manage" element={<PageManageFieldsV2 />} />
            <Route element={<AdminOnlyRoute />}>
              <Route path="/farms/outlook" element={<PageFieldsAnalyseV3 />} />
              <Route
                path="/farms/field-outlook"
                element={<PageFieldsAnalyseV4 />}
              />
              <Route path="/farms/dashboard" element={<PageFieldDashboard />} />
              <Route path="/farms/weather" element={<PageWeatherForecast />} />
            </Route>
            <Route element={<ForecastDashboardOnlyRoute />}>
              <Route
                path="/farms/dashboard-forecast"
                element={<PageFieldDashboardForecast />}
              />
            </Route>
          </Route>
        </Route>
        {/* <Route path="/test-error" element={<TestErrorPage />} /> TEST ROUTE. Uncomment and navigate to test ErrorBoundary catcher */}
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </RoutesBox>
  );
};

export default MyRoutes;
